import { recordOriginalGeometryPositions } from '../modelGeometry/meshOriginalGeometry';
import {
  getBoxInObjectSpace,
  recomputeGeometryBounds,
  setVertexFromObjectLocal,
} from '../modelGeometry/objectSpaceGeometry';
import { resizeFrontLineNineSlice } from '../modelGeometry/frontLineNineSliceResizer';
import {
  isCabinetSectionHighlightBox,
  withSectionHighlightBoxesHidden,
} from '../cabinetInterior/tallCabinetSectionHighlightBoxes';
import {isRuntimeCabinetHandle} from '../cabinetLoader/cabinetHandleRuntime';

// Ratio of each original segment kept near its ends before the middle stretch is distributed.
const DEFAULT_SIDE_BAND_RATIO = 0.12;

function recordWidthDepthBounds(instance, boundsTag) {
  if (instance.userData[boundsTag]) {
    return instance.userData[boundsTag];
  }

  const box = withSectionHighlightBoxesHidden(instance, () => {
    instance.updateMatrixWorld(true);
    return getBoxInObjectSpace(instance, instance);
  });
  const bounds = {
    minX: box.min.x,
    maxX: box.max.x,
    width: box.max.x - box.min.x,
    minZ: box.min.z,
    maxZ: box.max.z,
    depth: box.max.z - box.min.z,
  };

  instance.userData[boundsTag] = bounds;
  return bounds;
}

function getPointCoordinate(point, axis) {
  return axis === 'x' ? point.x : point.y;
}

// Converts 2D corner shape control points into model-space source/target knots for one axis.
function buildAxisMappingKnots(originalPoints, currentPoints, axis, sourceMin, sourceMax) {
  if (
    !Array.isArray(originalPoints)
    || !Array.isArray(currentPoints)
    || originalPoints.length !== currentPoints.length
    || originalPoints.length === 0
  ) {
    return null;
  }

  const originalCoordinates = originalPoints.map((point) => getPointCoordinate(point, axis));
  const currentCoordinates = currentPoints.map((point) => getPointCoordinate(point, axis));
  const originalMin = Math.min(...originalCoordinates);
  const originalMax = Math.max(...originalCoordinates);
  const currentMin = Math.min(...currentCoordinates);
  const originalSize = originalMax - originalMin;
  const sourceSize = sourceMax - sourceMin;

  if (!originalSize || originalSize <= 0 || !sourceSize || sourceSize <= 0) {
    return null;
  }

  const groups = {};

  originalCoordinates.forEach((originalCoordinate, index) => {
    const key = Math.round10(originalCoordinate, -4);
    groups[key] = groups[key] || {
      originalCoordinate,
      currentTotal: 0,
      count: 0,
    };
    groups[key].currentTotal += currentCoordinates[index];
    groups[key].count += 1;
  });

  const knots = Object.values(groups)
    .map((group) => {
      const currentCoordinate = group.currentTotal / group.count;

      return {
        source: sourceMin + (((group.originalCoordinate - originalMin) / originalSize) * sourceSize),
        target: sourceMin + (((currentCoordinate - currentMin) / originalSize) * sourceSize),
      };
    })
    .sort((a, b) => a.source - b.source);

  if (knots.length < 2) {
    return null;
  }

  return knots;
}

// Keeps both ends of each interval more stable and puts most resize delta into the interval middle.
function getMappedSegmentAxisValue(value, start, end, sideBandRatio) {
  const sourceSize = end.source - start.source;
  const targetSize = end.target - start.target;

  if (!sourceSize || sourceSize <= 0 || !targetSize || targetSize <= 0) {
    return value;
  }

  const sourceBandSize = sourceSize * sideBandRatio;
  const targetBandSize = Math.min(sourceBandSize, targetSize * 0.45);
  const sourceMinBandEnd = start.source + sourceBandSize;
  const sourceMaxBandStart = end.source - sourceBandSize;
  const targetMinBandEnd = start.target + targetBandSize;
  const targetMaxBandStart = end.target - targetBandSize;

  if (value <= sourceMinBandEnd) {
    return start.target + ((value - start.source) * (targetBandSize / sourceBandSize));
  }

  if (value >= sourceMaxBandStart) {
    return end.target - ((end.source - value) * (targetBandSize / sourceBandSize));
  }

  const sourceMiddleSize = sourceMaxBandStart - sourceMinBandEnd;
  const targetMiddleSize = targetMaxBandStart - targetMinBandEnd;

  if (sourceMiddleSize <= 0 || targetMiddleSize < 0) {
    return value;
  }

  const middleRatio = (value - sourceMinBandEnd) / sourceMiddleSize;
  return targetMinBandEnd + (middleRatio * targetMiddleSize);
}

// Maps a source x/z coordinate through the nearest pair of 2D-derived knots.
function getMappedAxisValue(value, knots, sideBandRatio) {
  if (!knots || knots.length < 2) {
    return value;
  }

  if (value <= knots[0].source) {
    return knots[0].target + (value - knots[0].source);
  }

  const lastKnot = knots[knots.length - 1];
  if (value >= lastKnot.source) {
    return lastKnot.target + (value - lastKnot.source);
  }

  for (let index = 0; index < knots.length - 1; index += 1) {
    const start = knots[index];
    const end = knots[index + 1];

    if (value >= start.source && value <= end.source) {
      return getMappedSegmentAxisValue(value, start, end, sideBandRatio);
    }
  }

  return value;
}

function resizeMeshLCornerWidthDepth(mesh, instance, mappings, options) {
  const originalGeometry = recordOriginalGeometryPositions(mesh, options.geometryTag, null, null);
  if (!originalGeometry) {
    return;
  }

  mesh.updateMatrixWorld(true);

  const THREE = window.THREE;
  const vertex = new THREE.Vector3();
  const originalVertex = new THREE.Vector3();
  const currentVertex = new THREE.Vector3();
  const instanceLocalVertex = new THREE.Vector3();
  const position = mesh.geometry.attributes.position;

  for (let index = 0; index < originalGeometry.positions.length; index += 3) {
    originalVertex.set(
      originalGeometry.positions[index],
      originalGeometry.positions[index + 1],
      originalGeometry.positions[index + 2]
    );
    currentVertex.set(
      position.array[index],
      position.array[index + 1],
      position.array[index + 2]
    );

    mesh.localToWorld(originalVertex);
    instance.worldToLocal(originalVertex);
    mesh.localToWorld(currentVertex);
    instance.worldToLocal(currentVertex);

    instanceLocalVertex.copy(currentVertex);
    instanceLocalVertex.x = getMappedAxisValue(
      originalVertex.x,
      mappings.x,
      options.sideBandRatio
    );
    instanceLocalVertex.z = getMappedAxisValue(
      originalVertex.z,
      mappings.z,
      options.sideBandRatio
    );

    setVertexFromObjectLocal(mesh, instance, vertex, instanceLocalVertex);

    position.array[index] = vertex.x;
    position.array[index + 1] = vertex.y;
    position.array[index + 2] = vertex.z;
  }

  position.needsUpdate = true;
  recomputeGeometryBounds(mesh.geometry);
}

// Resizes an L/corner cabinet by mapping each vertex through the edited 2D polygon, then applies front-specific 9-slice.
export function resizeCornerModelWidthDepth(instance, dimensions, options = {}) {
  if (
    !instance
    || !Array.isArray(dimensions.originalPoints)
    || !Array.isArray(dimensions.currentPoints)
  ) {
    return;
  }

  const resizeOptions = {
    boundsTag: options.boundsTag || 'lCornerModelWidthDepthOriginalBounds',
    geometryTag: options.geometryTag || 'lCornerModelWidthDepthOriginalGeometry',
    sideBandRatio: options.sideBandRatio || DEFAULT_SIDE_BAND_RATIO,
  };
  const originalBounds = recordWidthDepthBounds(instance, resizeOptions.boundsTag);

  if (
    !originalBounds.width
    || originalBounds.width <= 0
    || !originalBounds.depth
    || originalBounds.depth <= 0
  ) {
    return;
  }

  const mappings = {
    x: buildAxisMappingKnots(dimensions.originalPoints, dimensions.currentPoints, 'x', originalBounds.minX, originalBounds.maxX),
    z: buildAxisMappingKnots(dimensions.originalPoints, dimensions.currentPoints, 'y', originalBounds.minZ, originalBounds.maxZ),
  };

  if (!mappings.x || !mappings.z) {
    return;
  }

  instance.traverse((child) => {
    if (
      child.isMesh
      && !isCabinetSectionHighlightBox(child)
      && !isRuntimeCabinetHandle(child)
    ) {
      resizeMeshLCornerWidthDepth(
        child,
        instance,
        mappings,
        resizeOptions
      );
    }
  });

  resizeFrontLineNineSlice(
    instance,
    {
      originalPoints: dimensions.originalPoints,
      currentPoints: dimensions.currentPoints,
      frontLineIndexes: dimensions.frontLineIndexes,
    },
    originalBounds,
    {
      geometryTag: resizeOptions.geometryTag,
      sideBandRatio: options.frontSideBandRatio || resizeOptions.sideBandRatio,
    }
  );
}

export const resizeLCornerModelWidthDepth = resizeCornerModelWidthDepth;
